import type { BroadcastSchedule } from '@/models/broadcast';
import { DateTime } from 'luxon';

/** Parse a schedule date/datetime value into the OU timezone. */
export const parseScheduleInTimezone = (
  value: Date | string | null | undefined,
  timezone: string,
): DateTime | null => {
  if (!value) return null;
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value).setZone(timezone);
    return dt.isValid ? dt : null;
  }
  const iso = DateTime.fromISO(value, { setZone: true });
  if (iso.isValid) return iso.setZone(timezone);
  const fallback = DateTime.fromJSDate(new Date(value)).setZone(timezone);
  return fallback.isValid ? fallback : null;
};

/**
 * Returns yyyy-MM-dd in OU timezone for schedule date fields.
 * Plain date-only strings are treated as OU wall-clock dates; full ISO values are converted.
 */
export const getScheduleDatePart = (
  value: Date | string | null | undefined,
  timezone: string,
): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
  }
  if (!timezone?.trim()) {
    return null;
  }
  const dt = parseScheduleInTimezone(value, timezone);
  return dt?.isValid ? dt.toISODate() : null;
};

/** Normalize schedule date fields to OU-local yyyy-MM-dd before persisting. */
export const normalizeBroadcastScheduleForSave = (
  schedule: BroadcastSchedule,
  timezone: string,
): BroadcastSchedule => {
  if (!timezone?.trim()) {
    return schedule;
  }

  const startDate = schedule.startDate
    ? getScheduleDatePart(schedule.startDate, timezone) ?? schedule.startDate
    : schedule.startDate;

  const recurrence = schedule.recurrence;
  const endDate =
    recurrence?.end === 'OnDate' && recurrence.endDate
      ? getScheduleDatePart(recurrence.endDate, timezone) ?? recurrence.endDate
      : recurrence?.endDate ?? null;

  return {
    ...schedule,
    startDate,
    recurrence: recurrence ? { ...recurrence, endDate } : recurrence,
  };
};
